import React, { useState, useEffect } from "react";
import moment from "moment";
import styles from "./statusBanner.module.scss";

const METRIC_SWAP_INTERVAL = 10 * 1000;

const TRACKING_ACTIVE_BEFORE = 4 * 60 * 60 * 1000;
const LOCATION_STALE_AFTER = 5 * 60 * 1000;
const dateFormat = "MMM DD hh:mm A";

const getEtaMinutes = eta => {
  if (!eta || !eta.etaSec) {
    return null;
  }

  return Math.max(1, Math.round(eta.etaSec / 60));
};

const getEtaKms = eta => {
  if (!eta || !eta.distanceMeters) {
    return null;
  }

  return Math.max(1, Math.round(eta.distanceMeters / 1000));
};

const isTrackingLinkActive = booking =>
  Date.now() >= booking.boardingPoint.time - TRACKING_ACTIVE_BEFORE;

// Resolves the stop the customer cares about next. Before boarding that is
// their own boarding point; once boarded it is the next station on the route
// (a rest stop, say), and at the end of the route, their drop point.
const getStationId = station =>
  station && (station.stationId || station.station?._id);

const getStationName = station =>
  station.station?.name || station.name || "the next stop";

const getUpcomingTarget = (booking, upcomingStation) => {
  const upcomingStationId = getStationId(upcomingStation);

  if (!booking.isOnboarded) {
    return {
      isUpcomingStation: upcomingStationId === booking.boardingPoint.stationId,
      name: `the ${booking.boardingPoint.name} boarding point`
    };
  }

  if (!upcomingStationId || upcomingStationId === booking.dropPoint.stationId) {
    return {
      isDropPoint: true,
      isUpcomingStation: true,
      name: "the drop point"
    };
  }

  return {
    isDropPoint: false,
    isUpcomingStation: true,
    name: getStationName(upcomingStation)
  };
};

// The live ETA is measured to the bus's next station. Before boarding, when
// that isn't the boarding point (the bus has stops to make first), fall back
// to the boarding point's expected reaching time instead.
const getMinutesToTarget = (eta, target) => {
  if (target.isUpcomingStation) {
    return getEtaMinutes(eta);
  }

  if (!eta || !eta.boardingPointReachingTime) {
    return null;
  }

  const minutes = Math.round(
    (eta.boardingPointReachingTime - Date.now()) / (60 * 1000)
  );
  return minutes > 0 ? minutes : null;
};

// Boarding is missed once the bus has been marked at the boarding point and
// the scheduled boarding time has passed, without this customer on board.
const isBoardingMissed = props =>
  !props.booking.isOnboarded &&
  props.boardingStationMarked &&
  Date.now() > props.booking.boardingPoint.time;

// After boarding, or once the boarding point is behind the bus, the badge
// shows the live ETA and distance to the next station. Before boarding it is
// measured to the boarding point.
const getBadgeMetrics = props => {
  const { booking, eta, upcomingStation } = props;
  const liveMetrics = { minutes: getEtaMinutes(eta), kms: getEtaKms(eta) };

  if (booking.isOnboarded || isBoardingMissed(props)) {
    return liveMetrics;
  }

  const target = getUpcomingTarget(booking, upcomingStation);
  if (target.isUpcomingStation) {
    return liveMetrics;
  }

  // The live distance is to the next station, not the boarding point, so
  // there is no distance to show.
  return { minutes: getMinutesToTarget(eta, target), kms: null };
};

const getStatus = props => {
  const {
    booking,
    eta,
    upcomingStation,
    lastlocation,
    lastDataRecievedAt
  } = props;

  if (!isTrackingLinkActive(booking)) {
    return {
      showIcon: true,
      title: "Tracking not started yet",
      subtitle: `Your Tracking link will be active after ${moment(
        booking.boardingPoint.time - TRACKING_ACTIVE_BEFORE
      ).format(dateFormat)}.`
    };
  }

  // lastDataRecievedAt is refreshed by the tracking socket on every GPS
  // update; the initial lastlocation fetch is only a fallback for first paint.
  const lastGpsAt =
    parseInt(lastDataRecievedAt, 10) ||
    parseInt(lastlocation && lastlocation.latlngDate, 10);

  if (lastGpsAt && Date.now() - lastGpsAt > LOCATION_STALE_AFTER) {
    return {
      showIcon: true,
      title: "Bus location unavailable",
      subtitle:
        "Bus location is currently unavailable, showing the last updated location"
    };
  }

  // The bus has already been marked at the boarding point without this
  // customer on board, so the boarding point is behind it. Point them at the
  // next station instead, which is also what the live ETA is measured to.
  if (isBoardingMissed(props)) {
    const minutes = getEtaMinutes(eta);
    const nextStationName = getStationId(upcomingStation)
      ? getStationName(upcomingStation)
      : null;

    return {
      title: "Boarding missed",
      subtitle:
        minutes && nextStationName
          ? `The bus has left ${booking.boardingPoint.name}. It will reach ${nextStationName} in ${minutes} Mins`
          : `The bus has left the ${booking.boardingPoint.name} boarding point.`
    };
  }

  // The copy names the stop the customer is waiting on — their boarding point
  // before boarding, the next station or drop point after.
  const target = getUpcomingTarget(booking, upcomingStation);

  if (eta && eta.delay) {
    const minutes = getMinutesToTarget(eta, target);
    return {
      title: "Bus is delayed",
      subtitle: minutes
        ? `The bus will reach ${target.name} in ${minutes} Mins`
        : "The bus is running behind schedule."
    };
  }

  if (!booking.isOnboarded) {
    return {
      title: "Bus arriving to boarding point",
      subtitle: `The bus will be arriving soon at the ${booking.boardingPoint.name} boarding point.`
    };
  }

  if (target.isDropPoint) {
    return {
      title: "Bus arriving at your drop point",
      subtitle: "The bus will be arriving soon at your drop point."
    };
  }

  return {
    title: `Bus arriving at ${target.name}`,
    subtitle: `The bus will be arriving soon at the ${target.name}.`
  };
};

const StatusBanner = props => {
  const { booking, tripStatus, isTrackingWorking, isMaxService } = props;
  const [showDistance, setShowDistance] = useState(false);

  const { minutes, kms } = getBadgeMetrics(props);
  const canSwap = !!minutes && !!kms;

  useEffect(() => {
    if (!canSwap) {
      return undefined;
    }

    const timer = setInterval(
      () => setShowDistance(current => !current),
      METRIC_SWAP_INTERVAL
    );

    return () => clearInterval(timer);
  }, [canSwap]);

  // The completed and cancelled screens carry their own full-bleed artwork,
  // with no status banner over it.
  if (tripStatus === "COMPLETED" || booking.status !== "CONFIRMED") {
    return null;
  }

  // Once the link is live but GPS isn't reporting, the artwork stands alone
  // until the bus actually starts its trip — after that the banner returns.
  if (
    isTrackingLinkActive(booking) &&
    !isTrackingWorking &&
    tripStatus !== "ONTRIP"
  ) {
    return null;
  }

  const status = getStatus(props);
  const showKms = showDistance && canSwap;

  return (
    <div className={isMaxService ? styles.bannerMaxx : styles.banner}>
      {status.showIcon || !minutes ? (
        <div className={styles.iconBox}>
          <div className={styles.spinner}>
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>
      ) : (
        <div className={styles.etaBox}>
          <span className={styles.etaValue}>{showKms ? kms : minutes}</span>
          <span className={styles.etaUnit}>{showKms ? "kms" : "mins"}</span>
        </div>
      )}
      <div className={styles.content}>
        <span className={styles.title}>{status.title}</span>
        <span className={styles.subtitle}>{status.subtitle}</span>
      </div>
    </div>
  );
};

export default StatusBanner;
